<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
        <div>
          <h2 class="text-white font-weight-bold mb-1">{{ $t('superadmin.allCompanies') }}</h2>
          <p class="text-muted small mb-0">{{ $t('superadmin.subtitle') }}</p>
        </div>
        <div>
          <router-link
            :to="{ name: 'CreateOwner' }"
            class="btn btn-primary"
          >
            <i class="mdi mdi-plus mr-1"></i>
            {{ $t('superadmin.onboardClientBtn') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="col-12 grid-margin">
      <div class="card">
        <div class="card-body">
          <!-- Filter / Search Toolbar -->
          <div class="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
            <div class="search-box" style="min-width: 320px; max-width: 440px; width: 100%;">
              <div class="input-group">
                <span class="input-group-text">
                  <i class="mdi mdi-magnify"></i>
                </span>
                <input
                  v-model.trim="search"
                  type="text"
                  class="form-control"
                  :placeholder="$t('superadmin.searchCompanies')"
                />
                <button v-if="search" class="btn btn-outline-secondary" type="button" @click="search = ''">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </div>
            <div class="text-muted small">
              {{ $t('common.all') }}: <span class="text-white font-weight-medium">{{ filteredItems.length }}</span>
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <table class="table align-middle" style="table-layout: fixed; width: 100%;">
              <thead>
                <tr>
                  <th style="width: 45px;">No</th>
                  <th style="width: 250px;">{{ $t('superadmin.companyName') }}</th>
                  <th style="width: 200px;">{{ $t('superadmin.ownerContact') }}</th>
                  <th style="width: 170px;">{{ $t('superadmin.playerQuota') }}</th>
                  <th style="width: 110px;">{{ $t('superadmin.expiryDate') }}</th>
                  <th style="width: 110px;">{{ $t('common.status') }}</th>
                  <th class="text-right" style="width: 120px; padding-right: 1rem;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(data, index) in paginatedOwner" :key="data.id || index">
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  <td>
                    <div class="d-flex align-items-center">
                      <div
                        class="rounded-md mr-2 text-white font-weight-bold d-flex align-items-center justify-content-center flex-shrink-0"
                        style="width: 36px; height: 36px; background: rgba(99, 102, 241, 0.15); border: 1px solid rgba(99, 102, 241, 0.3); border-radius: 8px;"
                      >
                        <img
                          v-if="data.company && data.company.logo"
                          :src="config.STORAGE_URL + data.company.logo"
                          alt="Logo"
                          style="max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 6px;"
                        />
                        <span v-else class="text-primary font-weight-bold" style="font-size: 14px;">
                          {{ ((data.company && data.company.name) || data.name || 'G')[0].toUpperCase() }}
                        </span>
                      </div>
                      <div class="overflow-hidden">
                        <router-link
                          :to="{ name: 'DetailOwner', params: { id: String(data.id) } }"
                          class="text-white font-weight-semibold text-decoration-none d-block text-truncate"
                          :title="(data.company && data.company.name) || '-'"
                        >
                          {{ (data.company && data.company.name) || '-' }}
                        </router-link>
                        <small class="text-muted d-block text-truncate">
                          {{ $t('superadmin.joinDate') }}: {{ formatDate(data.created_at) }}
                        </small>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div class="text-white font-weight-medium text-truncate" :title="data.name">{{ data.name || '-' }}</div>
                    <small class="text-muted d-block text-truncate" :title="data.email">{{ data.email || '-' }}</small>
                  </td>
                  <td>
                    <div class="d-flex justify-content-between small mb-1">
                      <span class="text-white font-weight-medium">
                        {{ data.players_count || 0 }} / {{ (data.company && data.company.number_of_players) || 100 }}
                      </span>
                      <span class="text-muted font-weight-medium">
                        {{ calculateQuotaPercent(data.players_count, (data.company && data.company.number_of_players) || 100) }}%
                      </span>
                    </div>
                    <div class="progress" style="height: 6px;">
                      <div
                        class="progress-bar bg-primary"
                        role="progressbar"
                        :style="{ width: `${calculateQuotaPercent(data.players_count, (data.company && data.company.number_of_players) || 100)}%` }"
                      ></div>
                    </div>
                  </td>
                  <td>
                    <span class="small" :class="isExpired(data.company && data.company.expired_date) ? 'text-danger' : 'text-light'">
                      {{ (data.company && data.company.expired_date) ? formatDate(data.company.expired_date) : '-' }}
                    </span>
                  </td>
                  <td>
                    <button
                      type="button"
                      class="btn badge p-1 border-0 cursor-pointer text-left"
                      :class="(data.company && data.company.is_active) !== 0 ? 'badge-success' : 'badge-danger'"
                      @click="toggleCompanyStatus(data)"
                      :title="$currentLocale === 'en' ? 'Click to toggle status' : 'Klik untuk ubah status'"
                    >
                      <i class="mdi mr-1" :class="(data.company && data.company.is_active) !== 0 ? 'mdi-check-circle' : 'mdi-alert-circle'"></i>
                      {{ (data.company && data.company.is_active) !== 0 ? $t('superadmin.statusActive') : $t('common.inactive') }}
                    </button>
                  </td>
                  <td class="text-right">
                    <div class="d-inline-flex align-items-center gap-1">
                      <router-link
                        :to="{ name: 'DetailOwner', params: { id: String(data.id) } }"
                        class="btn btn-action-icon btn-outline-info"
                        :title="$t('common.view')"
                      >
                        <i class="mdi mdi-eye"></i>
                      </router-link>
                      <router-link
                        :to="{ name: 'EditOwner', params: { id: String(data.id) } }"
                        class="btn btn-action-icon btn-outline-warning"
                        :title="$t('common.edit')"
                      >
                        <i class="mdi mdi-pencil"></i>
                      </router-link>
                      <button
                        type="button"
                        class="btn btn-action-icon"
                        :class="(data.company && data.company.is_active) !== 0 ? 'btn-outline-danger' : 'btn-outline-success'"
                        @click="toggleCompanyStatus(data)"
                        :title="(data.company && data.company.is_active) !== 0 ? ($currentLocale === 'id' ? 'Tangguhkan Perusahaan' : 'Suspend Enterprise') : ($currentLocale === 'id' ? 'Aktifkan Perusahaan' : 'Activate Enterprise')"
                      >
                        <i class="mdi" :class="(data.company && data.company.is_active) !== 0 ? 'mdi-pause-circle-outline' : 'mdi-play-circle-outline'"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="paginatedOwner.length === 0">
                  <td colspan="7" class="text-center py-5">
                    <EmptyState
                      icon="mdi-domain"
                      :title="$t('superadmin.allCompanies')"
                      :description="$t('superadmin.subtitle')"
                      :actionText="$t('superadmin.onboardClientBtn')"
                      actionTo="/owner/add"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="filteredItems.length > 0" class="mt-3">
            <PaginationComponents
              :totalItems="filteredItems.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getOwner, updateOwner } from '../../services/ownerService';
import moment from 'moment';
import Swal from 'sweetalert2';
import PaginationComponents from '../../components/PaginationComponents.vue';
import EmptyState from '../../components/EmptyState.vue';
import config from '../../config/config';

export default {
  name: 'ListOwnerView',
  components: {
    PaginationComponents,
    EmptyState,
  },
  data() {
    return {
      search: '',
      owner: [],
      currentPage: 1,
      itemsPerPage: 10,
      config,
      loading: false,
    };
  },
  computed: {
    filteredItems() {
      if (!this.search) return this.owner;
      const q = this.search.toLowerCase();
      return this.owner.filter((item) => {
        const nameMatch = (item.name || '').toLowerCase().includes(q);
        const emailMatch = (item.email || '').toLowerCase().includes(q);
        const companyMatch = (item.company && item.company.name)
          ? item.company.name.toLowerCase().includes(q)
          : false;
        return nameMatch || emailMatch || companyMatch;
      });
    },
    paginatedOwner() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredItems.slice(start, end);
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
    },
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const response = await getOwner();
        this.owner = response || [];
      } catch (err) {
        console.error('Error fetching companies:', err);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD MMM YYYY');
    },
    isExpired(date) {
      if (!date) return false;
      return moment(date).isBefore(moment());
    },
    calculateQuotaPercent(used, total) {
      if (!total || total === 0) return 0;
      return Math.min(100, Math.round(((used || 0) / total) * 100));
    },
    onPageChanged(page) {
      this.currentPage = page;
    },
    async toggleCompanyStatus(data) {
      const currentActive = (data.company && data.company.is_active) !== 0;
      const newStatus = currentActive ? 0 : 1;
      const isEn = this.$i18n?.locale === 'en';
      const actionText = newStatus === 1 ? (isEn ? 'Activate' : 'Aktifkan') : (isEn ? 'Suspend' : 'Tangguhkan');
      const companyName = (data.company && data.company.name) || data.name || 'Perusahaan';

      const result = await Swal.fire({
        title: isEn ? `${actionText} Enterprise?` : `${actionText} Perusahaan?`,
        text: isEn
          ? `Change operational status for ${companyName}?`
          : `Ubah status operasional untuk ${companyName}?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: newStatus === 1 ? '#10b981' : '#ef4444',
        cancelButtonColor: '#64748b',
        confirmButtonText: isEn ? `Yes, ${actionText}` : `Ya, ${actionText}`,
        cancelButtonText: isEn ? 'Cancel' : 'Batal',
        background: '#141724',
        color: '#fff',
      });

      if (result.isConfirmed) {
        try {
          await updateOwner(data.id, {
            is_active: newStatus,
            status: newStatus === 1 ? 'active' : 'inactive'
          });
          if (data.company) {
            data.company.is_active = newStatus;
          }
          data.status = newStatus === 1 ? 'active' : 'inactive';
          Swal.fire({
            title: isEn ? 'Updated!' : 'Berhasil!',
            text: isEn ? 'Company status successfully updated.' : 'Status perusahaan berhasil diperbarui.',
            icon: 'success',
            timer: 1500,
            showConfirmButton: false,
            background: '#141724',
            color: '#fff',
          });
        } catch (err) {
          console.error(err);
          Swal.fire({
            title: this.$t('common.error'),
            text: err?.response?.data?.error || (isEn ? 'Failed to update company status' : 'Gagal memperbarui status perusahaan'),
            icon: 'error',
            background: '#141724',
            color: '#fff',
          });
        }
      }
    }
  },
};
</script>

<style scoped>
.btn-action-icon {
  width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 6px !important;
  font-size: 14px !important;
  line-height: 1 !important;
  transition: all 0.15s ease-in-out;
}
.btn-action-icon:hover {
  transform: translateY(-1px);
}
.cursor-pointer {
  cursor: pointer;
}
</style>
